import { Edit, RemoveRedEye } from '@mui/icons-material'
import { Box, Button, styled, TextField, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { FormFieldType } from '../../../enums'
import { FormField, FormRow } from '../../../generated-types/form-field'
import { IFormSchemaGeneric, IFormSchemaVersion, SchemaType } from '../../../generated-types/form-schema'
import { generateUUID } from '../../../utils/function'
import Form from './Form'
import FormGenericData from './FormGenericData'
import FormSettings from './FormSettings'

interface IFormGeneratorProps {
  buttonTitle: string
  onSubmit: (value: FormRow[], details: IFormSchemaGeneric) => void
  initialData?: IFormSchemaVersion
  isEdit?: boolean
  onCancel: () => void
  schemaType?: SchemaType
}

const FormGenerator: React.FC<IFormGeneratorProps> = ({
  buttonTitle = 'Save',
  onSubmit,
  onCancel,
  isEdit = false,
  initialData,
  schemaType = SchemaType.FormSchema,
}) => {
  const [layouts, setLayouts] = useState<FormRow[]>([{ columns: [{ fields: [] }] }])
  const [fieldDetails, setFieldDetails] = useState<IFormSchemaGeneric>({
    name: '',
    description: '',
    hasVideoUpload: false,
    isActive: false,
  })
  const [selectedFieldType, setSelectedFieldType] = useState(0)
  const [selectedSetting, setSelectedSettingField] = useState<FormField>()
  const [selectedFieldPosition, setSelectedFieldPosition] = useState({ row: 0, column: 0, field: 0 })
  const [rearrangeProperty, setRearrangeProperty] = useState({ layIndex: -1, colIndex: -1, fieldIndex: -1 })
  const [openTab, setOpenTab] = useState(0)
  const { t } = useTranslation(['form-schema', 'common'])
  const onDrag = (value: number) => {
    setSelectedFieldType(value)
  }
  const { showDialog, showConfirmDialog } = useDialogCtx()

  useEffect(() => {
    if (isEdit && initialData) {
      setFieldDetails({
        name: initialData?.formSchema?.name || '',
        description: initialData?.formSchema?.description || '',
        hasVideoUpload: initialData?.formSchema?.hasVideoUpload || false,
        isActive: initialData?.formSchema?.isActive || false,
      })
      const parsedLayout = JSON.parse(initialData?.layout || '')
      setLayouts(parsedLayout)
    }
  }, [isEdit, initialData])

  const onDrop = useCallback(
    (layoutIndex, columnIndex, fieldIndex) => {
      const fieldArray = layouts
      if (selectedFieldType != 0) {
        const field: FormField = {
          type: selectedFieldType,
          name: t('field-name'),
          identifier: generateUUID(),
        }
        if (fieldArray[layoutIndex].columns[columnIndex].fields.length < fieldIndex) {
          fieldArray[layoutIndex].columns[columnIndex].fields.push(field)
        } else {
          fieldArray[layoutIndex].columns[columnIndex].fields.splice(fieldIndex, 0, field)
        }
      } else if (rearrangeProperty.layIndex != -1) {
        const field =
          fieldArray[rearrangeProperty.layIndex].columns[rearrangeProperty.colIndex].fields[
            rearrangeProperty.fieldIndex
          ]
        if (field) {
          fieldArray[rearrangeProperty.layIndex].columns[rearrangeProperty.colIndex].fields.splice(
            rearrangeProperty.fieldIndex,
            1,
          )
          if (fieldArray[layoutIndex].columns[columnIndex].fields.length < fieldIndex) {
            fieldArray[layoutIndex].columns[columnIndex].fields.push(field)
          } else {
            fieldArray[layoutIndex].columns[columnIndex].fields.splice(fieldIndex, 0, field)
          }
        }
      }
      setLayouts([...fieldArray])
      setRearrangeProperty({ layIndex: -1, colIndex: -1, fieldIndex: -1 })
      setSelectedFieldType(0)
    },
    [layouts, selectedFieldType, rearrangeProperty],
  )

  const addLayout = (rowIndex, columnIndex: number | null = null) => {
    const layoutsList = layouts
    if (columnIndex == null) {
      layoutsList.splice(rowIndex, 0, { columns: [{ fields: [] }] })
    } else {
      layoutsList[rowIndex].columns.splice(columnIndex, 0, { fields: [] })
    }
    setLayouts([...layoutsList])
  }

  const deleteLayout = useCallback(
    (rowIndex, colIndex) => {
      showConfirmDialog(t('delete-column'), t('delete-column-content'), undefined, pop => {
        const layoutsList = layouts
        layoutsList[rowIndex].columns.splice(colIndex, 1)
        if (layoutsList[rowIndex].columns.length == 0) {
          layoutsList.splice(rowIndex, 1)
        }
        setLayouts([...layoutsList])
        pop()
      })
    },
    [layouts],
  )

  const selectFormField = (field: FormField, rowIndex: number, columnIndex: number, fieldIndex: number) => {
    setSelectedSettingField({ ...field })
    setSelectedFieldPosition({ row: rowIndex, column: columnIndex, field: fieldIndex })
    setOpenTab(1)
  }

  const updateField = (field: FormField, rowIndex: number, columnIndex: number, fieldIndex: number) => {
    const layoutsList = layouts
    layoutsList[rowIndex].columns[columnIndex].fields[fieldIndex] = field
    setLayouts([...layoutsList])
  }

  const onSubmitForm = () => {
    const contentFieldTypes = [FormFieldType.richText, FormFieldType.instructionImage, FormFieldType.instructionFile]
    const isQuillEmpty = (content: string) => !content || content.replace(/<[^>]*>/g, '').trim() === ''
    const missingContent = layouts.some(row =>
      row.columns.some(col =>
        col.fields.some(f => {
          if (!contentFieldTypes.includes(f.type)) return false
          if (f.type === FormFieldType.richText) return isQuillEmpty(f.content || '')
          return !f.content
        }),
      ),
    )

    if (fieldDetails.name == '') {
      toast.error(t('form-title-required'))
    } else if (missingContent) {
      toast.error(t('instruction-field-content-required'))
    } else {
      onSubmit(layouts, fieldDetails)
    }
  }

  const viewForm = () => {
    showDialog(
      Form,
      {
        componentProps: {
          form: undefined,
          disabled: false,
          onDrop: () => {},
          addLayout: () => {},
          deleteLayout: () => {},
          selectFormField: () => {},
          layouts: layouts,
          title: fieldDetails.name,
          onRearrange: () => {},
          cancelEnabled: true,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }

  const onDelete = () => {
    const layoutsList = layouts
    layoutsList[selectedFieldPosition.row].columns[selectedFieldPosition.column].fields.splice(
      selectedFieldPosition.field,
      1,
    )
    setLayouts([...layoutsList])
    setOpenTab(0)
    setSelectedSettingField(undefined)
  }

  return (
    <Box height={window.innerHeight - 375 + 'px'}>
      <Box borderBottom={1}>
        <Box padding={1} paddingLeft={3} display={'flex'} flexDirection={'row'} justifyContent={'space-evenly'}>
          <Box flex={3}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {t('form-generator')}
            </Typography>
          </Box>
          <Box flex={3}>
            <TextField
              placeholder={t('title', { ns: 'common' })}
              variant="standard"
              value={fieldDetails.name}
              disabled
            />
            <Edit
              style={{ cursor: 'pointer' }}
              onClick={() => {
                showDialog(FormGenericData, {
                  componentProps: {
                    fieldDetails: fieldDetails || {},
                    setFieldDetails: setFieldDetails,
                  },
                  opts: {
                    maxWidth: 'md',
                    fullWidth: true,
                  },
                })
              }}
            />
          </Box>
          <Box width={220} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
            <StyleRemoveRedEye onClick={() => viewForm()} />
            <Box width={180} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
              <Button
                variant="contained"
                onClick={() => {
                  onSubmitForm()
                }}
              >
                {buttonTitle}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  onCancel()
                }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box display={'flex'} flexDirection={'row'} width={'100%'}>
        <FormSettings
          onDelete={() => {
            onDelete()
          }}
          onDrag={onDrag}
          openTab={openTab}
          setOpenTab={setOpenTab}
          setLayouts={setLayouts}
          selectedSetting={selectedSetting}
          selectedRowIndex={selectedFieldPosition.row}
          selectedColumnIndex={selectedFieldPosition.column}
          selectedFieldIndex={selectedFieldPosition.field}
          updateField={updateField}
          setSelectedSettingField={setSelectedSettingField}
          schemaType={schemaType}
        />
        <Form
          form={undefined}
          title={fieldDetails.name}
          layouts={layouts}
          disabled={true}
          onDrop={onDrop}
          addLayout={addLayout}
          deleteLayout={deleteLayout}
          withColorLayout={true}
          selectFormField={selectFormField}
          onRearrange={(layIndex, colIndex, fieldIndex) => setRearrangeProperty({ layIndex, colIndex, fieldIndex })}
          onContentChange={(field, rowIndex, colIndex, fieldIndex) => {
            updateField(field, rowIndex, colIndex, fieldIndex)
            setSelectedSettingField(prev =>
              field.identifier && prev?.identifier === field.identifier ? { ...field } : prev,
            )
          }}
        />
      </Box>
    </Box>
  )
}

const StyleRemoveRedEye = styled(RemoveRedEye)({
  cursor: 'pointer',
  marginTop: 5,
})

export default FormGenerator
